"use client";

import createQueryString from "@/utils/query/createQueryString";
import { useSearchParams } from "next/navigation";
import { memo } from "react";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../ui/pagination";

export const CustomPagination = memo(function CustomPagination({
  pagesCount,
}: {
  pagesCount: number;
}) {
  const searchParams = useSearchParams();
  const currentPage = searchParams?.get("page") || 1;
  const { floor, min, max } = Math;
  const range = (lo: number, hi: number) =>
    Array.from({ length: hi - lo }, (_, i) => i + lo);

  const pagination =
    (count: number, ellipsis = true) =>
    (page: number, total: number) => {
      const start = max(
        1,
        min(page - floor((count - 3) / 2), total - count + 2)
      );
      const end = min(total, max(page + floor((count - 2) / 2), count - 1));
      return [
        ...(start > 2 ? [1, ellipsis] : start > 1 ? [1] : []),
        ...range(start, end + 1),
        ...(end < total - 1 ? [ellipsis, total] : end < total ? [total] : []),
      ];
    };

  return (
    <Pagination className="pt-4 md:pt-2">
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            href={`?${createQueryString(
              "page",
              `${Math.max(+currentPage - 1, 1)}`,
              searchParams
            )}`}
            aria-disabled={+currentPage <= 1}
            tabIndex={+currentPage <= 1 ? -1 : undefined}
            className={`!p-2 ${
              +currentPage <= 1 ? "pointer-events-none opacity-0" : undefined
            }`}
          />
        </PaginationItem>
        {pagination(4)(+currentPage, pagesCount)?.map((e, i) =>
          e === true ? (
            <PaginationItem key={`keyforpaginationellip${i}`}>
              <PaginationEllipsis className="w-8 sm:w-10 h-8 sm:h-10 text-primary-200" />
            </PaginationItem>
          ) : (
            <PaginationItem className="" key={`keyforpagination${i}`}>
              <PaginationLink
                className={`w-8 sm:w-10 h-8 sm:h-10 text-primary-200 ${
                  +currentPage === e ? "text-primary bg-primary-50" : ""
                }`}
                href={
                  +currentPage === e
                    ? "#"
                    : `?${createQueryString("page", `${e}`, searchParams)}`
                }
              >
                {e}
              </PaginationLink>
            </PaginationItem>
          )
        )}
        <PaginationItem>
          <PaginationNext
            href={`?${createQueryString(
              "page",
              `${+currentPage + 1}`,
              searchParams
            )}`}
            aria-disabled={+currentPage >= +pagesCount}
            tabIndex={+currentPage >= +pagesCount ? -1 : undefined}
            className={`!p-2 ${
              +currentPage >= +pagesCount
                ? "pointer-events-none opacity-0"
                : undefined
            }`}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
});
